import { isEmpty } from "lodash";

import React, { useCallback, useEffect, useRef, useState } from "react";

import HorizontalScrollBar from "@/components/HorizontalScrollBar";
import { ROUTES_PAGE_CHANGE_CABIN } from "@/constants/eventName";
import { BANK_NAME_MAP } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";

import { FilterContextProvider } from "./FilterContext";
import FilterList from "./components/FilterList";
import { getFilterDefault } from "./filters";
import ExtraCategory from "./filters/ExtraCategory";
import { useFilterList } from "./hooks/useFilterList";
import { useFilterOptions } from "./hooks/useFilterOptions";
import { useFilterOptionsCount } from "./hooks/useFilterOptionsCount";
import { FilterData, FilterFieldKey, FilterSourceData } from "./typing";

interface FilterBarProps {
  dataSourceList: FilterSourceData;
  onChange: (result: FilterSourceData, filter?: FilterData) => void;
  initValues?: Partial<FilterData>;
}

const FilterBar = (props: FilterBarProps) => {
  const { onChange } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState(false);
  const [fieldId, setFieldId] = useState<FilterFieldKey>("all");

  const defaultFilterData = useRef<FilterData>(getFilterDefault());
  const [filterData, setFilterData] = useState<FilterData>(() => {
    return {
      ...defaultFilterData.current,
      ...props.initValues,
    };
  });

  const filterOptions = useFilterOptions(props.dataSourceList);

  const data = useFilterList(props.dataSourceList, filterData);

  const filterOptionsCount = useFilterOptionsCount(props.dataSourceList, data);

  const { user_preference } = useGlobalStoreContext();

  // 监听全局变量，自动设置用户偏好过滤项目
  useEffect(() => {
    if (isEmpty(props.initValues)) {
      const data = {};
      if (user_preference?.programs?.length > 0) {
        Object.assign(data, { programs: user_preference.programs });
      }
      if (user_preference?.cards?.length > 0) {
        Object.assign(data, { banks: user_preference.cards.map((k) => BANK_NAME_MAP[k]) });
      }
      setFilterData((state) => ({
        ...state,
        ...data,
      }));
    }
  }, [user_preference, props.initValues]);

  useListenEvent(ROUTES_PAGE_CHANGE_CABIN, (v: string) => {
    setFilterData({
      ...filterData,
      cabins: [v],
    });
  });

  useEffect(() => {
    onChange && onChange(data);
  }, [data, onChange]);

  const resetFilterField = useEvent((key: keyof FilterData) => {
    setFilterData({
      ...filterData,
      [key]: defaultFilterData.current[key],
    });
  });

  const resetFilterData = useEvent(() => {
    setFilterData({
      ...defaultFilterData.current,
    });
  });

  const handleChangeFilter = useCallback(
    (value: Partial<FilterData>) => {
      setFilterData({
        ...filterData,
        ...value,
      });
    },
    [filterData]
  );

  return (
    <FilterContextProvider
      value={{
        filterOptions,
        filterOptionsCount,
        filterData,
        defaultFilterData: defaultFilterData.current,
        setFilterData: handleChangeFilter,
        anchorEl,
        setAnchorEl,
        open,
        setOpen,
        fieldId,
        setFieldId,

        resetFilterField,
        resetFilterData,
      }}
    >
      <FilterList />
      <div className="mt-[10px]">
        <HorizontalScrollBar>
          <div className="w-[100%] flex">
            <ExtraCategory />
          </div>
        </HorizontalScrollBar>
      </div>
    </FilterContextProvider>
  );
};

export default React.memo(FilterBar);
